import {useState, useRef, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {debounce, without} from 'lodash'

import {index} from '../../../../apis'
import {useIsMounted, useClickedOutsideComponent} from '../../../../hooks'

import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import CancelIcon from '@mui/icons-material/Cancel'


// Emotion CSS-in-JS styles
const styles = {
  root: css`
    position: relative;
    width: 100%;
  `,
  suggestions: css`
    z-index: 100;
    padding: 10px;
    position: absolute;
    width: 100%;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    background: white;
  `,
  chipsContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 10px 0;
    min-height: 30px;
  `,
  chip: css`
    margin: 2px;
    border-radius: 4px;
    color: #fff;
    background-color: #262626;
    text-transform: uppercase;
    cursor: pointer;
  `
}

/**
 * TagField for Formik: fetches suggestions and manages selected tags.
 */
const TagField = ({
  field: { name, value: initialValue },
  form:  { touched, errors, setFieldValue, isSubmitting },
  disabled,
  helperText,
  ...props
}) => {
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const isMounted = useIsMounted()
  const clickOutsideCount = useClickedOutsideComponent(containerRef)

  const [suggestions, setSuggestions] = useState([])
  const [selected, setSelected] = useState(initialValue || [])
  const [open, setOpen] = useState(false)

  const showError = touched[name] && Boolean(errors[name])
  const errorText = showError ? errors[name] : helperText


  // Debounced fetch tags
  const fetchTags = useCallback(
    debounce(async (e) => {
      try {
        const { data } = await index('/tags/search', { query: e.target.value })
        if (isMounted.current) {
          setSuggestions(data.data.map(item => item.attributes.tag))
        }
      } catch {
        // ignore
      }
    }, 300), [isMounted])


  // Close dropdown & clear suggestions on outside click
  useEffect(() => {
    setOpen(false)
    setSuggestions([])
  }, [clickOutsideCount])

  // Sync selected tags with form value
  useEffect(() => {
    setSelected(initialValue || [])
  }, [initialValue])

  // Update Formik & local state
  const updateSelection = (tags) => {
    setSelected(tags)
    setFieldValue(name, tags)
  }

  const addTag = (tag) => {
    if (selected.includes(tag) || selected.length >= 10) return
    updateSelection([...selected, tag])
    inputRef.current.value = ''
  }

  const removeTag = (tag) => () => {
    updateSelection(without(selected, tag))
  }

  const handleSelect = (tag) => () => {
    addTag(tag)
    setOpen(false)
    setSuggestions([])
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && suggestions.includes(e.target.value)) {
      handleSelect(e.target.value)
    }
  }


  function openContainer() {
    setOpen(true)
  }


  return (
    <div css={styles.root} ref={containerRef}>
      <TextField
        name={name}
        fullWidth
        error={showError}
        helperText={errorText}
        disabled={disabled ?? isSubmitting}
        inputRef={inputRef}
        onFocus={openContainer}
        onChange={fetchTags}
        onKeyDown={handleKeyDown}
        {...props}
      />

      {open && suggestions.length > 0 && (
        <Paper css={styles.suggestions} elevation={2}>
          {suggestions.map(tag => (
            <Chip
              key={tag}
              label={tag}
              onClick={handleSelect(tag)}
              css={styles.chip}
              sx={{
                '&:hover': { backgroundColor: '#32CD32' }
              }}
            />
          ))}
        </Paper>
      )}

      {Boolean(selected.length) && 
        <div css={styles.chipsContainer}>
          {selected.map(tag => (
            <Chip
              key={tag}
              label={tag}
              size='small'
              onDelete={removeTag(tag)}
              deleteIcon={<CancelIcon style={{color: 'grey'}} />}
              css={styles.chip}
            />
          ))}
        </div>
      }
    </div>
  )
}

export default TagField
